import styled, { css, keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { theme, typography } from "../Homepage/styles";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const RegisterPage = styled.div`
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: ${theme.bg};
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
`;

export const RegisterGlow = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0 0 auto;
  height: 420px;
  background-image: ${theme.gradientHero};
`;

export const RegisterShell = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 64px;
`;

export const RegisterMain = styled.main`
  display: flex;
  flex: 1;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px;
`;

export const RegisterInner = styled.div`
  width: 100%;
  max-width: 42rem; /* max-w-2xl */
`;

export const RegisterCard = styled.div`
  padding: 20px;
  border: 1px solid hsl(var(--hp-border) / 0.5);
  border-radius: 1rem;
  background: hsl(var(--hp-card) / 0.8);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  box-shadow: ${theme.shadowCard};

  @media (min-width: 640px) {
    padding: 40px;
  }
`;

export const RegisterCardHeader = styled.div`
  margin-bottom: ${({ $compact }) => ($compact ? "24px" : "40px")};
  text-align: ${({ $align }) => $align || "center"};
`;

export const RegisterTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-size: ${({ $compact }) => ($compact ? "1.5rem" : "1.875rem")};
  font-weight: ${typography.weights.bold};
  letter-spacing: ${typography.tracking.tight};
  line-height: 1.2;
`;

export const RegisterSubtitle = styled.p`
  margin: ${({ $compact }) => ($compact ? "4px" : "8px")} 0 0;
  color: ${theme.mutedForeground};
  font-size: ${({ $compact }) => ($compact ? "0.875rem" : "0.9375rem")};
  line-height: 1.5;
`;

export const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 40px; /* space-y-10 */
`;

export const RegisterSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px; /* space-y-5 */
`;

export const RegisterSectionHeading = styled.h2`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.3;
`;

export const RegisterStepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
`;

export const RegisterAlert = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 16px 16px 16px 44px;
  border: 1px solid hsl(0 72% 48% / 0.5);
  border-radius: 0.5rem;
  color: hsl(0 72% 48%);
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
  animation: ${fadeIn} 0.6s ease-out forwards;

  html[data-theme="dark"] & {
    border-color: hsl(0 84.2% 60.2%);
    color: hsl(0 84.2% 60.2%);
  }

  svg {
    position: absolute;
    top: 16px;
    left: 16px;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

export const RegisterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const RegisterLabel = styled.label`
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  line-height: 1;
`;

export const RegisterHint = styled.p`
  margin: 0;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  line-height: 1.4;
`;

export const RegisterInputWrap = styled.div`
  position: relative;
`;

export const RegisterInputIcon = styled.span`
  pointer-events: none;
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  display: flex;
  color: ${theme.mutedForeground};
  transform: translateY(-50%);

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const RegisterInputSuffix = styled.span`
  pointer-events: none;
  position: absolute;
  top: 50%;
  right: 12px;
  z-index: 1;
  color: ${theme.mutedForeground};
  font-size: 0.75rem;
  line-height: 1;
  transform: translateY(-50%);
`;

const inputBase = css`
  display: flex;
  width: 100%;
  height: 2.75rem;
  padding: 8px 12px;
  border: 1px solid hsl(var(--hp-border));
  border-radius: 0.375rem;
  background: hsl(var(--hp-background) / 0.5);
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  line-height: 1.4;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${theme.mutedForeground};
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${theme.bg},
      0 0 0 4px hsl(var(--hp-ring));
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &[aria-invalid="true"] {
    border-color: hsl(0 72% 48%);

    &:focus-visible {
      box-shadow:
        0 0 0 2px ${theme.bg},
        0 0 0 4px hsl(0 72% 48% / 0.35);
    }
  }
`;

export const RegisterInput = styled.input`
  ${inputBase}
  padding-left: ${(p) => (p.$withIcon ? "36px" : "12px")};
  padding-right: ${(p) =>
    p.$withClear ? "40px" : p.$withSuffix ? "48px" : "12px"};
  font-family: ${(p) => (p.$mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : typography.fontFamily)};
  letter-spacing: ${(p) => (p.$trackingWide ? "0.15em" : p.$trackingWidest ? "0.1em" : "normal")};
`;

export const RegisterTermsRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const RegisterCheckbox = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  margin-top: 2px;
  padding: 0;
  border: 1px solid hsl(var(--hp-border));
  border-radius: 0.25rem;
  background: hsl(var(--hp-background));
  color: ${theme.primaryForeground};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &[aria-checked="true"] {
    background: ${theme.primary};
    border-color: ${theme.primary};
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${theme.bg},
      0 0 0 4px hsl(var(--hp-ring));
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const RegisterTermsLabel = styled.label`
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.normal};
  line-height: 1.375;
  cursor: pointer;
`;

export const RegisterTermsLink = styled(Link)`
  color: ${theme.primary} !important;
  text-decoration: none;
  text-underline-offset: 4px;

  &:hover,
  &:focus,
  &:active {
    color: ${theme.primary} !important;
    text-decoration: underline;
    text-decoration-color: ${theme.primary};
  }
`;

export const RegisterPlanCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid hsl(var(--hp-primary) / 0.4);
  border-radius: 0.75rem;
  background: hsl(var(--hp-primary) / 0.05);
`;

export const RegisterPlanTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

export const RegisterPlanName = styled.div`
  color: ${theme.foreground};
  font-weight: ${typography.weights.semibold};
`;

export const RegisterPlanDesc = styled.div`
  margin-top: 2px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  line-height: 1.45;
  white-space: pre-line;
`;

export const RegisterPlanPriceCol = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

export const RegisterPlanPriceOld = styled.div`
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-decoration: line-through;
`;

export const RegisterPlanPrice = styled.div`
  color: ${theme.foreground};

  span:first-child {
    font-size: 1.5rem;
    font-weight: ${typography.weights.bold};
    line-height: 1.2;
  }
`;

export const RegisterPlanPeriod = styled.span`
  margin-left: 2px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
`;

export const RegisterPlanDiscountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid hsl(var(--hp-primary) / 0.2);
  font-size: 0.875rem;
`;

export const RegisterPlanDiscountLabel = styled.span`
  color: ${theme.mutedForeground};
`;

export const RegisterPlanDiscountValue = styled.span`
  color: ${theme.primary};
  font-weight: ${typography.weights.semibold};
`;

export const RegisterCouponApplied = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-muted) / 0.3);
`;

export const RegisterCouponAppliedText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.foreground};
  font-size: 0.875rem;
  line-height: 1.4;

  > svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${theme.primary};
  }
`;

export const RegisterCouponCode = styled.span`
  font-weight: ${typography.weights.semibold};
`;

export const RegisterCouponSave = styled.span`
  color: ${theme.primary};
  font-weight: ${typography.weights.semibold};
`;

export const RegisterCouponActions = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;
`;

export const RegisterCouponChange = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: ${theme.primary};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: hsl(var(--hp-primary) / 0.1);
  }
`;

export const RegisterCouponRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  border-radius: 9999px;
  background: transparent;
  color: ${theme.mutedForeground};
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const RegisterCouponRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
`;

export const RegisterCouponField = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

/** Lovable: clear (X) inside coupon input when text is present */
export const RegisterCouponClear = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${theme.mutedForeground};
  cursor: pointer;
  transform: translateY(-50%);
  transition: color 0.15s ease, background 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
    background: hsl(var(--hp-muted) / 0.5);
  }
`;

export const RegisterApplyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: auto;
  min-height: 44px;
  height: 2.75rem;
  padding: 0 1.25rem;
  border: 1px solid hsl(var(--hp-primary) / 0.75);
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${theme.primary};
    background: hsl(var(--hp-primary) / 0.08);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const RegisterCouponError = styled.p`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 8px 0 0;
  color: hsl(0 72% 48%);
  font-size: 0.875rem;
  line-height: 1.4;

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const RegisterPayTabs = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$single ? "1fr" : "1fr 1fr")};
  gap: 4px;
  padding: 4px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-muted) / 0.3);
`;

export const RegisterPayTab = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 12px;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.mutedForeground};
  font-family: ${typography.fontFamily};
  font-size: 0.875rem;
  font-weight: ${typography.weights.medium};
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &[aria-selected="true"] {
    background: hsl(var(--hp-card));
    color: ${theme.foreground};
    box-shadow: 0 1px 2px hsl(0 0% 0% / 0.06);
  }

  &:hover:not(:disabled):not([aria-selected="true"]) {
    color: ${theme.foreground};
  }

  &:disabled {
    cursor: default;
  }
`;

export const RegisterCardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;

  @media (min-width: 640px) {
    &.exp-cvc {
      grid-template-columns: 1fr 1fr;
      gap: 16px;
    }
  }
`;

export const RegisterBitcoinNote = styled.div`
  padding: 24px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-muted) / 0.3);
  text-align: center;

  > svg,
  > img {
    display: block;
    width: 32px;
    height: 32px;
    margin: 0 auto 8px;
    color: ${theme.primary};
  }

  > img {
    object-fit: contain;
  }

  p {
    margin: 0;
    color: ${theme.mutedForeground};
    font-size: 0.875rem;
    line-height: 1.5;
  }
`;

export const RegisterInvoicePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  border: 1px solid hsl(var(--hp-primary) / 0.4);
  border-radius: 0.75rem;
  background: hsl(var(--hp-primary) / 0.05);
`;

export const RegisterInvoiceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.foreground};
  font-size: 0.875rem;
  font-weight: ${typography.weights.semibold};

  svg {
    width: 20px;
    height: 20px;
    color: ${theme.primary};
  }
`;

export const RegisterInvoiceFrame = styled.div`
  overflow: hidden;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-background) / 0.5);

  iframe {
    display: block;
    width: 100%;
    height: 750px;
    border: none;
  }
`;

export const RegisterInvoiceWaiting = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;

  svg {
    width: 16px;
    height: 16px;
    color: ${theme.primary};
  }
`;

export const RegisterTotalRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 0.75rem;
  background: hsl(var(--hp-muted) / 0.2);
`;

export const RegisterTotalLabel = styled.span`
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
`;

export const RegisterTotalValue = styled.span`
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.bold};
`;

export const RegisterSubmit = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 3rem;
  padding: 0 2rem;
  border: none;
  border-radius: 0.5rem;
  background-image: ${theme.gradientGold};
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  font-weight: ${typography.weights.bold};
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    transform: scale(1.03);
    box-shadow: 0 6px 30px hsl(32 95% 55% / 0.5);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
    filter: saturate(0.5);
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${theme.bg},
      0 0 0 4px hsl(var(--hp-ring)),
      0 4px 20px hsl(32 95% 55% / 0.4);
  }
`;

export const RegisterIncompleteHint = styled.p`
  margin: 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-align: center;
`;

export const RegisterTrust = styled.p`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  color: ${theme.mutedForeground};
  font-size: ${({ $compact }) => ($compact ? "0.75rem" : "0.875rem")};
  font-weight: 400;
  line-height: 1.25;
  text-align: left;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    color: ${theme.primary};
  }

  span {
    min-width: 0;
  }
`;

export const RegisterSubmitBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

export const RegisterEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 24px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 1rem;
  background: hsl(var(--hp-muted) / 0.2);
  text-align: center;
`;

export const RegisterEmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 9999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};

  svg {
    width: 28px;
    height: 28px;
  }
`;

export const RegisterEmptyTitle = styled.h2`
  margin: 0;
  color: ${theme.foreground};
  font-size: 1.25rem;
  font-weight: ${typography.weights.semibold};
`;

export const RegisterEmptyText = styled.p`
  margin: 0;
  max-width: 28rem;
  color: ${theme.mutedForeground};
  font-size: 0.9375rem;
  line-height: 1.5;
`;

export const RegisterEmptyActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding-top: 8px;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

export const RegisterHeroBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  height: 3rem;
  padding: 0 2rem;
  border: none;
  border-radius: 0.5rem;
  background-image: ${theme.gradientGold};
  box-shadow: 0 4px 20px hsl(32 95% 55% / 0.4);
  color: ${theme.primaryForeground};
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  font-weight: ${typography.weights.bold};
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }

  @media (min-width: 640px) {
    width: auto;
    min-width: 10rem;
  }
`;

export const RegisterOutlineBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-width: 0;
  height: 3rem;
  padding: 0 2rem;
  border: 1px solid ${theme.primary};
  border-radius: 0.5rem;
  background: transparent;
  color: ${theme.primary} !important;
  font-family: ${typography.fontFamily};
  font-size: 1rem;
  font-weight: ${typography.weights.semibold};
  text-decoration: none !important;
  transition: background-color 0.15s ease;

  &:hover {
    background: hsl(var(--hp-primary) / 0.08);
  }

  @media (min-width: 640px) {
    width: auto;
    min-width: 10rem;
  }
`;

export const RegisterSpinIcon = styled.span`
  display: inline-flex;
  animation: register-spin 0.8s linear infinite;

  @keyframes register-spin {
    to {
      transform: rotate(360deg);
    }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const RegisterFooterNote = styled.p`
  margin: 24px 0 0;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-align: center;
`;

export const RegisterFooterLink = styled(Link)`
  color: ${theme.primary} !important;
  font-weight: ${typography.weights.semibold};
  text-decoration: none;
  text-underline-offset: 4px;

  &:hover,
  &:focus,
  &:active {
    color: ${theme.primary} !important;
    text-decoration: underline;
    text-decoration-color: ${theme.primary};
  }
`;

export const RegisterHomeWrap = styled.div`
  margin-top: 32px;
  text-align: center;
`;

export const RegisterHomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${theme.mutedForeground};
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.15s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.foreground};
  }
`;
